// JID-2360: render-ready projection of a local refill's inventory ledger. While the refill runs it is what
// the fridge has registered so far (step 3); once it has finished it is the refill summary (step 5). Lives
// outside transactionSlice because it needs selectSelectedLanguage, which already imports transactionSlice.
import { createSelector } from "@reduxjs/toolkit";
import type { RootState } from "store";
import { type Dish, type Refill, TransactionMode } from "types";
import type { AppLanguage, LocalDishType } from "types/mainTypes";
import { selectSelectedLanguage } from "./appStateSlice";
import { selectRefill } from "./transactionSlice";

export type RefillSummaryRow = {
	// `null` for a dish whose `type` is missing entirely; otherwise the dish type id.
	dishTypeId: string | null;
	// `null` => the type is not in state.dishes.types (or carries no name) => render the unknown-dish copy.
	name: string | null;
	image: LocalDishType["image"];
	pieces: number;
};

export type RefillSummarySectionData = {
	total: number;
	rows: RefillSummaryRow[];
};

export type RefillSummaryData = {
	added: RefillSummarySectionData;
	removed: RefillSummarySectionData;
};

export const EMPTY_REFILL_SUMMARY: RefillSummaryData = {
	added: { total: 0, rows: [] },
	removed: { total: 0, rows: [] },
};

// The slot's refill in any state, unless it is remote: RemoteRefillModal owns those. A plain function, not
// createSelector: it hands the refill through unchanged, which a memoized selector would only warn about.
const selectLocalRefill = (state: RootState): Refill | null => {
	const refill = selectRefill(state);
	return refill && refill.mode !== TransactionMode.Remote ? refill : null;
};

const buildSection = (
	dishes: Dish[] | null | undefined,
	catalog: Map<string, LocalDishType>,
	language: AppLanguage,
): RefillSummarySectionData => {
	const list = dishes ?? [];
	const pieceCounts = new Map<string | null, number>();
	for (const dish of list) {
		// A dish with no type keeps its own bucket instead of being dropped, so the rows still add up.
		const dishTypeId = dish.type?.id ?? null;
		pieceCounts.set(dishTypeId, (pieceCounts.get(dishTypeId) ?? 0) + 1);
	}
	const rows = [...pieceCounts].map(([dishTypeId, pieces]): RefillSummaryRow => {
		const dishType = dishTypeId === null ? undefined : catalog.get(dishTypeId);
		return {
			dishTypeId: dishTypeId,
			name: dishType?.description?.[language]?.name ?? dishType?.description?.primary?.name ?? null,
			// `null` makes DishTypeImage fall back to the placeholder image.
			image: dishType?.image ?? null,
			pieces: pieces,
		};
	});
	// Known names first, case/diacritics-insensitive; unknown rows are appended last so a missing name
	// never sorts ahead of real dishes.
	rows.sort((a, b) =>
		a.name === null || b.name === null
			? Number(a.name === null) - Number(b.name === null)
			: a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
	);
	// The section total is the ledger length, never the sum of the rows.
	return { total: list.length, rows: rows };
};

// `state.dishes.types` is read raw (like selectOrderDishTypes) — selectDishTypes filters out types
// without price/description, which would turn a perfectly known dish into "Unknown dish".
export const selectRefillSummary = createSelector(
	[selectLocalRefill, (state: RootState) => state.dishes.types, selectSelectedLanguage],
	(refill, dishTypes, language): RefillSummaryData => {
		if (!refill) {
			return EMPTY_REFILL_SUMMARY;
		}
		const catalog = new Map((dishTypes ?? []).map((dishType) => [dishType.id, dishType]));
		return {
			added: buildSection(refill.inventory?.added, catalog, language),
			removed: buildSection(refill.inventory?.removed, catalog, language),
		};
	},
);

// A number so OrderProcessDrawer re-renders only when the row count changes; 0 without a local refill.
export const selectRefillSummaryRowCount = createSelector(
	selectRefillSummary,
	(summary): number => summary.added.rows.length + summary.removed.rows.length,
);
